import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * MarketInsightDetailPage — "Customer Insights" drill-down for a single
 * customer at a single merchant. Content-only, no sidebar/header, dropped
 * into the existing shell. Reuses the same rp-* classes / DataTable used
 * across the rest of the Restaurant Manager Portal for visual consistency.
 * ---------------------------------------------------------------------- */

const STATUS_BADGE = {
  matched: { label: "Matched", className: "rp-badge--returning" },
  pending: { label: "Pending", className: "rp-badge--new" },
  unmatched: { label: "Unmatched", className: "rp-badge--grey" },
};

const DEFAULT_PERIOD = "last_30_days";
const PERIOD_OPTIONS = [
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "year_to_date", label: "Year to Date" },
  { value: "all_time", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const formatDate = (value) => {
  if (!value || value === "Unknown") return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const StatCard = ({ label, value, accent }) => (
  <div className="rp-stat-card">
    <div className="rp-stat-card__top">
      <span className="rp-stat-card__label">{label}</span>
    </div>
    <div className="rp-stat-card__body">
      <div className="rp-stat-card__value-wrap">
        <span
          className="rp-stat-card__value"
          style={accent ? { color: accent } : undefined}
        >
          {value}
        </span>
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.unmatched;
  return <span className={`rp-badge ${meta.className}`}>{meta.label}</span>;
};

const HistorySection = ({
  title,
  subtitle,
  rows,
  columns,
  loadingMore,
  onLoadMore,
  hasMore,
}) => (
  <div className="rp-card di-history-card">
    <div className="rp-card__header">
      <div>
        <h3 className="rp-card__title">{title}</h3>
        {subtitle && <p className="rp-card__subtitle">{subtitle}</p>}
      </div>
    </div>
    <DataTable
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.id}
      loading={false}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
    />
  </div>
);

const CustomerDetailPage = () => {
  document.title = "Customer Insight Detail | Omaha Food Lovers";
  const [searchParams] = useSearchParams();
  const merchantName = searchParams.get("merchant_name");
  const customerId = searchParams.get("customer_id");

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState({
    period: DEFAULT_PERIOD,
    dateRange: { start: "", end: "" },
  });
  const [draftPeriod, setDraftPeriod] = useState(DEFAULT_PERIOD);
  const [draftDateRange, setDraftDateRange] = useState({ start: "", end: "" });
  const [dateRangeError, setDateRangeError] = useState("");
  const filterToolbarRef = useRef(null);
  const detailRequestIdRef = useRef(0);

  const [txPage, setTxPage] = useState(1);
  const [txLoadingMore, setTxLoadingMore] = useState(false);
  const [visitPage, setVisitPage] = useState(1);
  const [visitLoadingMore, setVisitLoadingMore] = useState(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const fetchDetail = async () => {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    setLoading(true);
    try {
      const { period, dateRange } = appliedFilters;
      const params = new URLSearchParams({
        merchant_name: merchantName || "",
        customer_id: customerId || "",
        period: period,
      });
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      const response = await get(
        `${endpoints.restaurantMarketInsightsShareOfWallet}?${params.toString()}`
      );
      if (requestId !== detailRequestIdRef.current) return;
      if (response.data?.success) {
        setDetail(response.data.data);
      }
    } catch (error) {
      if (requestId !== detailRequestIdRef.current) return;
      console.error("Failed to fetch customer insight detail:", error);
      toastr.error("Failed to load customer detail");
    } finally {
      if (requestId === detailRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantName, customerId, appliedFilters]);

  useEffect(() => {
    setDraftPeriod(appliedFilters.period);
    setDraftDateRange(appliedFilters.dateRange);
  }, [appliedFilters]);

  useEffect(() => {
    const restoreDraftFilters = () => {
      setDraftPeriod(appliedFilters.period);
      setDraftDateRange(appliedFilters.dateRange);
      setDateRangeError("");
    };

    const handleClick = (event) => {
      if (filterToolbarRef.current && !filterToolbarRef.current.contains(event.target)) {
        restoreDraftFilters();
      }
    };

    const handleKey = (event) => {
      if (event.key === "Escape") {
        restoreDraftFilters();
      }
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [appliedFilters]);

  const resetHistoryPagination = () => {
    setTxPage(1);
    setVisitPage(1);
  };

  const applyFilters = (periodValue, rangeValue = { start: "", end: "" }) => {
    const nextRange = periodValue === "custom" ? rangeValue : { start: "", end: "" };
    setAppliedFilters((prev) => {
      const unchanged =
        prev.period === periodValue &&
        prev.dateRange.start === nextRange.start &&
        prev.dateRange.end === nextRange.end;

      if (unchanged) return prev;

      resetHistoryPagination();
      return {
        period: periodValue,
        dateRange: nextRange,
      };
    });
  };

  const handleDraftPeriodChange = (value) => {
    setDraftPeriod(value);
    setDateRangeError("");
    if (value !== "custom") {
      const emptyRange = { start: "", end: "" };
      setDraftDateRange(emptyRange);
      applyFilters(value, emptyRange);
    }
  };

  const handleDateRangeChange = (value) => {
    setDraftDateRange({ start: value?.start || "", end: value?.end || "" });
    setDateRangeError("");
  };

  const handleApplyFilters = () => {
    if (draftPeriod !== "custom") {
      applyFilters(draftPeriod);
      return;
    }

    if (!draftDateRange.start || !draftDateRange.end) {
      setDateRangeError("Select both start and end dates.");
      return;
    }

    if (draftDateRange.end < draftDateRange.start) {
      setDateRangeError("End date cannot be before start date.");
      return;
    }

    applyFilters("custom", draftDateRange);
  };

  const hasCustomDraft = draftPeriod === "custom";
  const canApplyCustom = !hasCustomDraft || Boolean(draftDateRange.start && draftDateRange.end && draftDateRange.end >= draftDateRange.start);

  const handleFilterToolbarClickCapture = (event) => {
    if (!event.target.closest(".ft-panel__done-btn")) return;
    if (!canApplyCustom) {
      event.preventDefault();
      event.stopPropagation();
      if (hasCustomDraft) {
        setDateRangeError(
          draftDateRange.start && draftDateRange.end
            ? "End date cannot be before start date."
            : "Select both start and end dates."
        );
      }
      return;
    }
    handleApplyFilters();
  };

  const loadMoreHistory = async (historyKey, page, setLoadingFlag, setPageFn) => {
    if (!detail?.[historyKey]?.next_page_url) return;
    setLoadingFlag(true);
    try {
      const params = new URLSearchParams({
        merchant_name: merchantName || "",
        customer_id: customerId || "",
        history_page: page + 1,
        period: appliedFilters.period,
      });
      if (appliedFilters.period === "custom") {
        params.append("date_from", appliedFilters.dateRange.start);
        params.append("date_to", appliedFilters.dateRange.end);
      }
      const response = await get(
        `${endpoints.restaurantMarketInsightsShareOfWallet}?${params.toString()}`
      );
      if (response.data?.success) {
        const nextHistory = response.data.data[historyKey];
        setDetail((prev) => ({
          ...prev,
          [historyKey]: {
            ...nextHistory,
            data: [...prev[historyKey].data, ...nextHistory.data],
          },
        }));
        setPageFn(page + 1);
      }
    } catch (error) {
      console.error(`Failed to load more ${historyKey}:`, error);
    } finally {
      setLoadingFlag(false);
    }
  };

  const transactionColumns = useMemo(
    () => [
      { key: "reference", label: "Reference", width: "1.4fr" },
      { key: "merchant_name", label: "Merchant", width: "1.4fr" },
      { key: "category", label: "Category", width: "1fr" },
      { key: "amount_display", label: "Amount", width: "1fr", align: "right" },
      { key: "points_earned", label: "Points Earned", width: "1fr", align: "right", render: (r) => <span className="nt-points">+{r.points_earned}</span> },
      { key: "transaction_status", label: "Status", width: "1fr", align: "center", render: (r) => <StatusBadge status={r.transaction_status} /> },
      { key: "transaction_date", label: "Date", width: "1.4fr", render: (r) => formatDate(r.transaction_date) },
    ],
    []
  );

  const visitColumns = useMemo(
    () => [
      { key: "merchant_name", label: "Merchant", width: "1.4fr" },
      { key: "restaurant_name", label: "Restaurant", width: "1.4fr" },
      { key: "amount_display", label: "Amount", width: "1fr", align: "right" },
      { key: "points_earned", label: "Points Earned", width: "1fr", align: "right", render: (r) => <span className="nt-points">+{r.points_earned}</span> },
      { key: "transaction_status", label: "Status", width: "1fr", align: "center", render: (r) => <StatusBadge status={r.transaction_status} /> },
      { key: "visited_at", label: "Visit Date", width: "1.4fr", render: (r) => formatDate(r.visited_at) },
    ],
    []
  );

  const redemptionColumns = useMemo(
    () => [
      { key: "redemption_reference", label: "Reference", width: "1.4fr" },
      { key: "restaurant", label: "Restaurant", width: "1.4fr" },
      { key: "voucher_or_reward", label: "Voucher/Reward", width: "1.6fr" },
      { key: "points", label: "Points", width: "1fr", align: "right" },
      { key: "value_display", label: "Value", width: "1fr", align: "right" },
      { key: "status", label: "Status", width: "1fr", align: "center" },
      { key: "redeemed_date", label: "Redeemed At", width: "1.4fr", render: (r) => formatDate(r.redeemed_date) },
    ],
    []
  );

  if (loading || !detail) {
    return (
      <div className="page-content rp-page-content">
        <Container fluid>
          <div className="rp-page di-skeleton-page">
            <div className="rp-skeleton" style={{ width: 220, height: 28, marginBottom: 20 }} />
            <div className="admin-dashboard-stats-grid">
              {Array.from({ length: 5 }).map((_, i) => (
                <div className="rp-stat-card" key={i}>
                  <div className="rp-skeleton" style={{ width: "60%", marginBottom: 12 }} />
                  <div className="rp-skeleton" style={{ width: "40%" }} />
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const { restaurant, period: apiPeriod, customer, merchant, summary, transaction_history, redemption_history, visit_history } = detail;
  const pointsBalance = Number(detail?.summary?.points_balance ?? 0);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          {/* ---- Header ---- */}
          <div className="di-header">
            <div className="di-header__text">
              <h2 className="rp-page-header__title di-header__title">
                {customer?.customer_name || "Unknown Customer"}
                <span className={`rp-badge di-merchant-badge ${merchant?.is_partner ? "rp-badge--returning" : "rp-badge--grey"}`}>
                  {merchant?.partner_status === "partner" ? "Partner" : "Non-Partner"}
                </span>
              </h2>
              <p className="rp-page-header__subtitle">
                {merchant?.merchant_name} · {apiPeriod?.label} ({apiPeriod?.start_date} – {apiPeriod?.end_date})
              </p>
            </div>
          </div>

          <div
            ref={filterToolbarRef}
            className="di-filter-toolbar--no-search"
            onClickCapture={handleFilterToolbarClickCapture}
          >
            <style>
              {`.di-filter-toolbar--no-search .ft-toolbar__search { display: none; }`}
            </style>
            <FilterToolbar
              filters={[
                {
                  key: "period",
                  type: "sortBy",
                  label: "Period",
                  value: draftPeriod,
                  onChange: handleDraftPeriodChange,
                  defaultValue: DEFAULT_PERIOD,
                  options: PERIOD_OPTIONS,
                },
                ...(draftPeriod === "custom" ? [{
                  key: "dateRange",
                  type: "dateRange",
                  label: "Date Range",
                  value: { preset: "custom", ...draftDateRange },
                  onChange: handleDateRangeChange,
                  defaultValue: { preset: "custom", start: "", end: "" },
                  hidePresets: true,
                }] : []),
              ]}
            />
            {dateRangeError && (
              <p className="custom-input-feedback show" style={{ marginTop: -12, marginBottom: 16 }}>
                {dateRangeError}
              </p>
            )}
          </div>

          <div style={{ marginBottom: 24 }}></div>

          {/* ---- Stat cards ---- */}
          <div className="admin-dashboard-stats-grid di-stats-grid">
            <StatCard label="Total Spend" value={summary?.total_spent_display || `$${((summary?.total_spent || 0) / 100).toFixed(2)}`} accent="var(--text-orange)" />
            <StatCard label="Transactions" value={summary?.total_transactions} />
            <StatCard label="Visits" value={summary?.total_visits} />
            <StatCard label="Points Earned" value={summary?.points_earned?.toLocaleString()} accent="#1ba97f" />
            <StatCard label="Available Points Balance" value={pointsBalance.toLocaleString()} />
          </div>

          {/* ---- Customer + Merchant info ---- */}
          <div className="di-info-grid">
            <div className="rp-card">
              <div className="rp-card__header">
                <h3 className="rp-card__title">Customer</h3>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">Email</span>
                <span className="di-info-row__value">{customer?.customer_email || "Unknown"}</span>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">Mobile</span>
                <span className="di-info-row__value">{customer?.customer_mobile || "Unknown"}</span>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">Last Visit</span>
                <span className="di-info-row__value">{formatDate(summary?.last_visit_at)}</span>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">First Transaction</span>
                <span className="di-info-row__value">{formatDate(summary?.first_transaction_at)}</span>
              </div>
            </div>

            <div className="rp-card">
              <div className="rp-card__header">
                <h3 className="rp-card__title">Merchant</h3>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">Restaurant</span>
                <span className="di-info-row__value">{restaurant?.title}</span>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">Category</span>
                <span className="di-info-row__value">{merchant?.category}</span>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">MCC</span>
                <span className="di-info-row__value">{merchant?.merchant_mcc || "—"}</span>
              </div>
              <div className="di-info-row">
                <span className="di-info-row__label">Source Merchant</span>
                <span className="di-info-row__value">{merchant?.source_merchant_names || "—"}</span>
              </div>
            </div>
          </div>

          {/* ---- History sections ---- */}
          <HistorySection
            title="Transaction History"
            subtitle={`${transaction_history?.total ?? 0} total transactions`}
            rows={transaction_history?.data || []}
            columns={transactionColumns}
            loadingMore={txLoadingMore}
            hasMore={!!transaction_history?.next_page_url}
            onLoadMore={() => loadMoreHistory("transaction_history", txPage, setTxLoadingMore, setTxPage)}
          />

          <HistorySection
            title="Visit History"
            subtitle={`${visit_history?.total ?? 0} total visits`}
            rows={visit_history?.data || []}
            columns={visitColumns}
            loadingMore={visitLoadingMore}
            hasMore={!!visit_history?.next_page_url}
            onLoadMore={() => loadMoreHistory("visit_history", visitPage, setVisitLoadingMore, setVisitPage)}
          />

          <HistorySection
            title="Redemption History"
            subtitle={
              (redemption_history?.total ?? 0) === 0
                ? "No redemptions in this period"
                : `${redemption_history?.total} total redemptions`
            }
            rows={redemption_history?.data || []}
            columns={redemptionColumns}
            loadingMore={false}
            hasMore={!!redemption_history?.next_page_url}
            onLoadMore={() => {}}
          />
        </div>
      </Container>
    </div>
  );
};

export default CustomerDetailPage;
